import type { MetaSchemaField } from "../../components/MetaSchemaEditor/MetaSchemaEditor";
import type { SchemaField } from "../../components/SchemaEditor/SchemaEditor";
import type { BotSource } from "./config/types";
import type { RosterRefusedLine, RosterRow } from "./types";

/**
 * The rules the two table tabs of the Bots page share: what a project's card
 * is made of, how one of its values is printed, and the CSV shapes the browser
 * builds for itself.
 *
 * They live in a module of their own rather than beside the table that uses
 * them most, because every one of them is used by at least two of the page's
 * files — and because a file that exports both a component and a helper is one
 * fast refresh cannot reload.
 */

/**
 * One page of a bots table.
 *
 * Source PAGES, because the table it draws is the one the backend was sized
 * for: migration 0059 reasons about «a source of ten thousand names» and
 * `ListRoster` has taken an offset from the start, so a screen that could only
 * ever reach the first fifty made row 3 000 findable by knowing its name and
 * no other way. The search box and the two filters are still how a KNOWN row
 * is reached; the pager is how the table is read.
 *
 * Guild templates and Pool have no pager: those tables are described in tens,
 * not thousands, and neither footer promises a range.
 */
export const PAGE_SIZE = 50;

/**
 * What a picker offers where the key may simply be absent. An empty box is
 * «this key is not given» — never a zero and never an empty string, which is
 * the whole point of a card being a card.
 */
export const NOT_GIVEN = "— not given —";

/**
 * `bot_roster_display_name_check` and `guild_template_name_check`, in the
 * browser: a long name is a refusal an operator should see before the round
 * trip. One constant because the two checks are the same check.
 */
export const MAX_NAME_RUNES = 64;

/**
 * The oldest anything on this page may claim to be — `maxGuildAgeDays` in
 * internal/bot/admin_templates.go, ten years.
 *
 * ONE constant for two questions, because it is one arithmetic limit rather
 * than two product decisions: `time.Duration(days) * 24 * time.Hour` overflows
 * past about 106 751 days and dates the row in the FUTURE instead of erroring,
 * so both the guild «Create guilds» stands up and the account a bot is created
 * with are held to the same ceiling. A typo of «300000» for «30» was enough to
 * reach it.
 *
 * It is here rather than in either dialog so the two cannot drift: the Create
 * guilds dialog, the Account age section and the roster row editor all refuse
 * the same number, and a refusal a form can prevent is not worth a round trip.
 */
export const MAX_AGE_DAYS = 3650;

/**
 * The most a CSV import may weigh.
 *
 * It is `maxImportBodyBytes` server-side, checked in the browser as well so a
 * file that overruns it is refused with a sentence rather than with a 413 the
 * operator has to interpret.
 */
export const MAX_CSV_BYTES = 2 << 20;

/** The two modes, in the words the Source tab's own switch uses. */
export const SOURCE_LABEL: Record<BotSource, string> = {
  generated: "Generated",
  table: "From table",
};

/**
 * A duplicate opens the editor rather than writing straight away, and its name
 * arrives already changed: neither table can hold two rows under one name, so
 * a copy under the same name is the one thing they refuse.
 */
export function copyName(name: string): string {
  return `${name} copy`.slice(0, MAX_NAME_RUNES);
}

/**
 * Why the worker skips this row, as a phrase rather than a column value.
 *
 * Today the one reason either table stores is `name_taken`. An unknown one is
 * printed as it arrived rather than swallowed: a server that grew a second
 * reason should show it here before anybody has to add a case.
 */
export function blockedLabel(reason: string | null | undefined): string {
  if (!reason) return "blocked";
  return reason === "name_taken" ? "name taken" : reason;
}

/** One declared card key, from the project's own schema. */
export interface CardKey {
  kind: "stat" | "meta";
  key: string;
  type: string;
  /** `meta_schema`'s allow-list, when the key has one. */
  allowed?: string[];
}

/**
 * The project's card, as one ordered list: stats first, then meta, each in the
 * order the project declared it. It is the order the CSV header uses, the
 * order the table draws and the order the row editor renders, because all
 * three are the schema — a second ordering would be a second answer to «what
 * is on this card».
 */
export function cardKeysOf(
  stats: SchemaField[],
  meta: MetaSchemaField[],
): CardKey[] {
  return [
    ...stats.map<CardKey>((f) => ({ kind: "stat", key: f.key, type: f.type })),
    ...meta.map<CardKey>((f) => ({
      kind: "meta",
      key: f.key,
      type: f.type,
      allowed: f.allowed,
    })),
  ];
}

/** The stable id of a card column — the CSV's own spelling of the key. */
export function columnID(k: CardKey): string {
  return `${k.kind}:${k.key}`;
}

/** Right-aligned and mono: the columns a schema declares as a number. */
export function isNumberType(k: CardKey): boolean {
  return k.type === "int" || k.type === "float" || k.type === "number";
}

/**
 * Why this box cannot be saved, or null. Only the numeric types can be typed
 * wrong — everything else in either row editor is a picker or free text.
 *
 * The value arrives as the editors hold it: text for the scalar types, a list
 * for `string[]`. A list is never a number, so it is never wrong here.
 */
export function numberError(
  k: CardKey,
  value: string | string[] | undefined,
): string | null {
  if (!isNumberType(k)) return null;
  const text = typeof value === "string" ? value.trim() : "";
  if (text === "") return null;
  const n = Number(text);
  if (!Number.isFinite(n)) return "Not a number.";
  if (k.type === "int" && !Number.isInteger(n)) return "A whole number.";
  return null;
}

/**
 * Which row an editor dialog is open on, and what it opened with. Both tables
 * hold it the same way — the row itself (null when the dialog will create
 * one), the values the boxes start from, and the title that says which of the
 * three things this is.
 */
export interface EditState<Row, Input> {
  row: Row | null;
  input: Input;
  title: string;
}

/** 1240 → «1 240», grouped with a NON-BREAKING space: a narrow cell that broke
 *  a number across two lines would read as two numbers. */
export function formatCount(n: number): string {
  return n.toLocaleString("en-US").replace(/,/g, "\u00a0");
}

/** «1 row» / «34 rows». A screen that prints the count a dozen times and says
 *  «1 rows» once reads as a template rather than as an answer. */
export function plural(n: number, one: string, many: string): string {
  return `${formatCount(n)} ${n === 1 ? one : many}`;
}

/**
 * Is this key carried by this card at all? An explicit `null` counts as
 * absent — that is what a null MEANS in a card, since it is the only way to
 * clear a declared key.
 */
export function carries(bag: Record<string, unknown>, key: string): boolean {
  return (
    !!bag && key in bag && bag[key] !== null && bag[key] !== undefined
  );
}

/** One stored value as a cell. Numbers are grouped, lists are joined, and a
 *  boolean is a word rather than the string "true". */
export function cellValue(value: unknown): string {
  if (typeof value === "number") return formatCount(value);
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (Array.isArray(value)) return value.map((v) => String(v)).join(", ");
  return String(value);
}

/**
 * The whole card in one line — `level 31 · trophies 1 240 · badge veteran`.
 *
 * A key with no value is left OUT rather than printed as zero: that is the
 * same rule a rolled card follows, and the difference between «this bot has no
 * badge» and «this bot has the badge called empty string».
 */
export function cardSummary(
  stats: Record<string, unknown>,
  meta: Record<string, unknown>,
  keys: CardKey[],
): string {
  const parts: string[] = [];
  for (const k of keys) {
    const bag = k.kind === "stat" ? stats : meta;
    if (!carries(bag, k.key)) continue;
    parts.push(`${k.key} ${cellValue(bag[k.key])}`);
  }
  return parts.join(" · ");
}

/** The same, over a whole row. */
export function rowCard(row: RosterRow, keys: CardKey[]): string {
  return cardSummary(row.stats, row.meta, keys);
}

/**
 * A row's own account age as a cell — «720 d», or a dash.
 *
 * The dash is the same dash Country and Behaviour print and means the same
 * thing: the row said nothing, so the number is DRAWN from `bots.account_age`
 * when the row is spent. It is emphatically not «0 days old», which is a value
 * a row can carry on purpose, and the table's footer says so where the column
 * is drawn.
 */
export function ageCell(days: number | null | undefined): string {
  return days === null || days === undefined ? "—" : `${formatCount(days)} d`;
}

/**
 * The header a project's roster file carries: the columns every project has,
 * then one column per declared card key in the project's own schema order. It
 * is RosterCSVHeader in Go without the `status` column an import ignores — the
 * two are the same list because they are the same schema.
 *
 * The fixed columns must match Go's ORDER as well as its names, `avatar`
 * second included. The round trip an operator makes is export → edit cell →
 * import `replace_unused`, and a header that disagreed with the server's would
 * read every avatar into the wrong column — or, with the column missing
 * altogether, delete the unused half of the table and write it back with none.
 *
 * `age_days` sits where it sits in the table and in the row editor, after the
 * fields that describe WHO the bot is and before the card: one order for the
 * file, the screen and the dialog, because an operator who exports a file,
 * edits a cell and imports it back is reading the same row in three places.
 */
export function rosterCsvHeader(cardKeys: CardKey[]): string {
  return [
    "name",
    "avatar",
    "country",
    "profile",
    "age_days",
    ...cardKeys.map(columnID),
  ].join(",");
}

/**
 * The header a project's templates file carries: the three columns every
 * project has, then one column per declared metadata key in the project's own
 * schema order. It is GuildTemplateCSVHeader in Go without the `status` column
 * an import ignores — the two are the same list because they are the same
 * schema.
 *
 * `logo` sits third, the order the table and the dialog put it in. A file
 * WITHOUT it still imports — the header is read by name — but this one carries
 * it, because the round trip an operator makes is export → edit → import, and
 * a header that dropped the column would take every logo with it.
 */
export function templateCsvHeader(metaKeys: CardKey[]): string {
  return ["name", "description", "logo", ...metaKeys.map(columnID)].join(",");
}

/**
 * The refused lines, back out as a file the operator fixes and re-imports.
 *
 * Each line keeps its ORIGINAL text, with the reason appended as one extra
 * column: a line in that file without its reason is a line they would have to
 * diagnose a second time.
 *
 * `header` must be the header of the FILE these lines came out of, not the
 * canonical one built from this project's schema. The lines are the operator's,
 * in their own column order and their own column count, and the two headers
 * disagree the moment a file comes from another environment — or on any project
 * with an empty `stats_schema`, where the Go export adds `stat:` columns from
 * the rows themselves. See ImportCsvResult.header.
 */
export function refusedCsv(
  header: string,
  lines: RosterRefusedLine[],
): string {
  const rows = [`${header},refused_reason`];
  for (const l of lines) rows.push(`${l.raw},${quote(l.message)}`);
  return `${rows.join("\r\n")}\r\n`;
}

function quote(s: string): string {
  return `"${s.replace(/"/g, '""')}"`;
}
